// Plain-language copy for the /admin/social scoreboard (#1561). The wording
// follows the prototype on the issue; the agent's own notes (decide.ts,
// gate.ts, executor.ts) are machine-facing, so every string staff read goes
// through one of these. Pure: no React, no network.
import type { CreativeTest, CreativeTestRun } from "@/hooks/admin/useCreativeTests";
import { PLATFORM_LABELS } from "../../supabase/functions/social-sync/mapping";
import { BUDGET_SERVICE_LABELS, type BudgetService } from "../../supabase/functions/social-enrich/services";
import type { ContentTypeTag } from "../../supabase/functions/social-enrich/tags";
import {
  MEDIA_UNAVAILABLE_TAG,
  NO_DASHES_TAG,
  scorePercentile,
  type PoolExclusion,
  type ScoredPost,
} from "../../supabase/functions/social-enrich/creative-tests";
import { TIMEZONE, planLinkForService, type Decision } from "../../supabase/functions/creative-test-agent/decide";
import type { AgentMode } from "../../supabase/functions/creative-test-agent/settings";
import { localDate } from "../../supabase/functions/lead-flow-monitor/monitor";
import type { NeedsNewPostsFlag, PillTone } from "./social-scoreboard";

export const MODE_LABELS: Record<AgentMode, string> = { off: "Off", shadow: "Practice", live: "Live" };

/** The ⓘ text on each live column of This week, verbatim from the prototype. */
export const STATUS_TIPS: Record<"status" | "spend" | "visits" | "cpv" | "leads" | "booked" | "views", string> = {
  booked: "Accepted quote totals from this ad's leads. The real money.",
  spend: "What this test ad has cost since it started this week.",
  visits: "People who clicked the ad and opened the quote planner. This is what ranks the ads.",
  cpv: "Spend divided by planner visits. Lowest wins. No visits yet means it ranks last.",
  leads: "Real quote requests from this ad, matched to an event. Not Meta's own lead count.",
  views: "Times the ad was shown. It needs 1,000 views and 7 days before it can be judged.",
  status:
    "Where it stands right now against the other test ads. The real call happens on judging day: the top third wins (if it got a planner visit or a lead), the middle gets one more week, the bottom third is cut.",
};

export const PILL_TONE_CLASSES: Record<PillTone, string> = {
  ok: "bg-status-ok-soft text-status-ok",
  warn: "bg-status-warn-soft text-status-warn",
  hot: "bg-status-hot-soft text-status-hot",
  info: "bg-status-info-soft text-status-info",
  mute: "bg-muted text-muted-foreground",
  acc: "bg-accent-soft text-accent",
};

/** "Oct 22" in Toronto time. */
export function torontoMonthDay(at: string | number): string {
  const ms = typeof at === "number" ? at : Date.parse(at);
  return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: TIMEZONE });
}

/** "Oct 22" for a Toronto calendar date (YYYY-MM-DD). Noon UTC, so the day can't slip in either direction. */
export function torontoDayLabel(date: string): string {
  return torontoMonthDay(`${date}T12:00:00Z`);
}

const moneyFormat = new Intl.NumberFormat("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const wholeFormat = new Intl.NumberFormat("en-CA", { maximumFractionDigits: 0 });

/** "$9.80" from cents; "—" when unknown. */
export function formatMoney(cents: number | null | undefined): string {
  return cents === null || cents === undefined ? "—" : `$${moneyFormat.format(cents / 100)}`;
}

/** A booked total, "$4,850" from cents; "—" when unknown or nothing booked. */
export function formatBooked(cents: number | null | undefined): string {
  return !cents ? "—" : `$${wholeFormat.format(cents / 100)}`;
}

/** "1st", "2nd", "3rd", "4th", "11th", "22nd". */
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  const suffix = ({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th";
  return `${n}${suffix}`;
}

const lowerFirst = (s: string) => (s ? s[0].toLowerCase() + s.slice(1) : s);
const upperFirst = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

/**
 * One write-gate reason (gate.ts checkWriteGate) as a plain sentence. Anything
 * not on the list is passed through as the agent wrote it.
 */
export function plainGateReason(reason: string): string {
  const mode = /^mode is (\w+), not live$/.exec(reason);
  if (mode) return `Mode is ${MODE_LABELS[mode[1] as AgentMode] ?? mode[1]}, not Live`;
  if (reason === "daily_budget_cents is not set") return "No daily budget set";
  if (reason === "monthly_cap_cents is not set") return "No monthly cap set";
  if (reason === "agent_activation_enabled is off") return "Ad activation is off (a super admin turns it on)";
  if (/^daily budget .* above max_daily_budget_cents/.test(reason)) return "Daily budget is above the allowed maximum";
  if (/^spend read failed/.test(reason)) return "Couldn't read spend from Meta";
  if (/^projected month spend/.test(reason)) return "This month's spend would pass the cap";
  if (/^test pot month-to-date/.test(reason)) return "This month's test budget is used up";
  return reason;
}

export interface SlotReason {
  slot: "reserved" | "flex" | null;
  poolRank: number | null;
  poolSize: number | null;
}

/**
 * The slot and pool rank from a pick's reason ("Reserved Wedding slot: pool
 * rank 1 of 8, …" or "Flex …"), also behind a "Live mode: … recorded as
 * shadow." prefix. Nulls when the reason doesn't say.
 */
export function parseSlotReason(reason: string | null): SlotReason {
  const m = /\b(Reserved|Flex) .+? slot: pool rank (\d+) of (\d+)/.exec(reason ?? "");
  if (!m) return { slot: null, poolRank: null, poolSize: null };
  return { slot: m[1] === "Flex" ? "flex" : "reserved", poolRank: Number(m[2]), poolSize: Number(m[3]) };
}

/** A dropped pick's log line: the live executor tags the drops it makes itself (#1618). */
function dropText(reason: string): string {
  if (reason.includes(MEDIA_UNAVAILABLE_TAG)) return "dropped: Meta didn't return its media";
  if (reason.includes(NO_DASHES_TAG)) return "dropped: its ad copy had a dash";
  return "dropped: it no longer qualifies";
}

/** "Why this post" in a row's detail. */
export function whyThisPost(input: {
  service: BudgetService;
  slot: "reserved" | "flex" | null;
  poolRank: number | null;
  score: ScoredPost | undefined;
}): string {
  const label = BUDGET_SERVICE_LABELS[input.service];
  const which = input.poolRank === null ? `a strong ${label} post` : input.poolRank === 1 ? `the best ${label} post` : `the ${ordinal(input.poolRank)}-best ${label} post`;
  const rate = input.score ? ` (${(input.score.engagement_rate * 100).toFixed(1)}%)` : "";
  if (input.slot === "flex") {
    return `The extra slot goes to the service with the most strong posts. This was ${which}${rate}.`;
  }
  if (!input.score) return `${upperFirst(which)} when it was picked.`;
  const platform = PLATFORM_LABELS[input.score.platform as keyof typeof PLATFORM_LABELS] ?? input.score.platform;
  const beats = Math.min(99, scorePercentile(input.score.score));
  return `${upperFirst(which)}: higher engagement than ${beats}% of our ${platform} posts${rate}.`;
}

/** A pool exclusion as the Post library's "Left out: <reason>". */
export const LEFT_OUT_LABELS: Record<PoolExclusion, string> = {
  "skipped by staff": "skipped by staff",
  "carousel post": "carousel",
  "unsupported media": "this format can't run as an ad",
  giveaway: "giveaway",
  contest: "contest",
  "time-limited promo": "limited-time offer",
  "dated post": "dated post",
  "seasonal post": "seasonal post",
};

const META_VERBS: Record<string, { done: string; todo: string }> = {
  ad_from_post: { done: "ad created", todo: "create the ad" },
  update_status: { done: "ad turned on", todo: "turn the ad on" },
  pause_ad: { done: "ad paused", todo: "pause the ad" },
};

const SIMULATED_TEXT: Partial<Record<Decision["kind"], string>> = {
  simulated_win: "would win",
  simulated_extend: "would get one more week",
  simulated_lose: "would be cut",
};

const VERDICT_TEXT: Partial<Record<Decision["kind"], string>> = { win: "won", lose: "cut", extend: "one more week" };

function waitText(reason: string): string {
  const m = /Not judged yet: ([\d,]+) of ([\d,]+) impressions, day (\d+) of (\d+)/.exec(reason);
  if (m) return `not judged yet: ${m[1]} of ${m[2]} views, day ${m[3]} of ${m[4]}`;
  const ext = /Extended: re-judged on day (\d+)/.exec(reason);
  if (ext) return `not judged yet: judged again on day ${ext[1]}`;
  return "not judged yet";
}

function pickText(service: BudgetService, reason: string | null): string {
  const label = BUDGET_SERVICE_LABELS[service];
  const { slot, poolRank, poolSize } = parseSlotReason(reason);
  if (poolRank === null || poolSize === null) return `picked for the ${label} test`;
  if (slot === "flex") return `picked for the extra slot (${ordinal(poolRank)} of ${poolSize} ${label} posts that qualify)`;
  return `picked for the ${label} test (${ordinal(poolRank)} of ${poolSize} that qualify)`;
}

type LogEntry = { at: number; text: string; dated: boolean };

/**
 * "What the agent did" for one test: its pick, each Meta write, the newest
 * wait, its verdict, a drop, practice verdicts and a staff stop, one dated
 * line each, oldest first. Repeated lines collapse to the newest. In practice
 * mode a test with no ad also gets "No ad made: practice mode."
 */
export function agentLog(
  test: Pick<CreativeTest, "id" | "social_post_id" | "service" | "created_at" | "reason" | "meta_ad_id">,
  runs: ReadonlyArray<Pick<CreativeTestRun, "started_at" | "decisions">>,
  mode: AgentMode,
): string[] {
  const service = test.service as BudgetService;
  const day = torontoMonthDay;
  const entries: LogEntry[] = [];
  let newestWait: LogEntry | null = null;
  let picked: LogEntry | null = null;
  const ordered = [...runs].sort((a, b) => a.started_at.localeCompare(b.started_at));
  for (const run of ordered) {
    const at = Date.parse(run.started_at);
    for (const d of run.decisions ?? []) {
      if (d.kind === "add") {
        if (picked || d.social_post_id !== test.social_post_id || d.service !== test.service) continue;
        if (localDate(at, TIMEZONE) !== localDate(Date.parse(test.created_at), TIMEZONE)) continue;
        picked = { at, text: pickText(service, d.reason), dated: true };
        entries.push(picked);
        continue;
      }
      if (d.test_id !== test.id) continue;
      if (d.kind === "meta_write" && d.meta) {
        const verbs = META_VERBS[d.meta.action];
        if (!verbs) continue;
        let text: string;
        if (d.meta.ok) text = verbs.done;
        else if (d.meta.refused && d.meta.refused.length > 0) text = `couldn't ${verbs.todo}: ${lowerFirst(plainGateReason(d.meta.refused[0]))}`;
        else text = `couldn't ${verbs.todo}: ${d.meta.error ?? "Meta returned an error"}`;
        entries.push({ at, text, dated: true });
      } else if (d.kind === "wait") {
        newestWait = { at, text: waitText(d.reason), dated: true };
      } else if (VERDICT_TEXT[d.kind]) {
        entries.push({ at, text: `judged: ${VERDICT_TEXT[d.kind]}`, dated: true });
      } else if (d.kind === "requalify_drop") {
        entries.push({ at, text: dropText(d.reason), dated: true });
      } else if (SIMULATED_TEXT[d.kind]) {
        entries.push({ at, text: `practice verdict from organic engagement: ${SIMULATED_TEXT[d.kind]}`, dated: true });
      }
    }
  }
  if (!picked) {
    picked = { at: Date.parse(test.created_at), text: pickText(service, test.reason), dated: true };
    entries.push(picked);
  }
  if (newestWait) entries.push(newestWait);
  if (mode === "shadow" && !test.meta_ad_id) entries.push({ at: picked.at, text: "No ad made: practice mode.", dated: false });
  for (const line of (test.reason ?? "").split("\n")) {
    const stop = /^Stopped by staff on (\d{4}-\d{2}-\d{2})/.exec(line.trim());
    if (stop) entries.push({ at: Date.parse(`${stop[1]}T16:00:00Z`), text: "stopped by staff", dated: true });
  }
  // Collapse repeats to the newest, then oldest first. The sort is stable, so
  // a line pushed after the pick at the same instant stays after it.
  const newest = new Map<string, LogEntry>();
  for (const e of entries) {
    const seen = newest.get(e.text);
    if (!seen || e.at >= seen.at) newest.set(e.text, e);
  }
  const kept = entries.filter((e) => newest.get(e.text) === e);
  kept.sort((a, b) => a.at - b.at);
  return kept.map((e) => (e.dated ? `${day(e.at)}: ${e.text}.` : e.text));
}

/** Content types as a plural noun phrase, for the gap box and the brief. */
export const CONTENT_TYPE_PHRASES: Record<ContentTypeTag, string> = {
  creator_talking: "people talking to camera",
  food_closeup: "food close-ups",
  event_setup: "event setups",
  team_people: "people enjoying the event",
  behind_scenes: "behind-the-scenes posts",
  testimonial: "client reviews",
  seasonal: "seasonal posts",
};

function typesPhrase(winning: readonly ContentTypeTag[]): string {
  const parts = winning.map((t) => CONTENT_TYPE_PHRASES[t]);
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

const NEEDS_WHY: Record<NeedsNewPostsFlag["reason"], string> = {
  cut_twice: "Cut two weeks running.",
  cut_then_on_track: "Cut last week and on track to be cut again.",
  low_pool: "",
};

/** The gap box: "<Service> needs new posts. <why> Only N of its posts can be tested. <Types> win most for other services." */
export function needsNewPostsText(flag: NeedsNewPostsFlag, winning: readonly ContentTypeTag[]): string {
  const label = BUDGET_SERVICE_LABELS[flag.service];
  const count = flag.poolSize === 0 ? "None of its posts can be tested." : `Only ${flag.poolSize} of its posts can be tested.`;
  const types = typesPhrase(winning);
  return [`${label} needs new posts.`, NEEDS_WHY[flag.reason], count, types ? `${upperFirst(types)} win most for other services.` : ""]
    .filter(Boolean)
    .join(" ");
}

/** The banner above the board: "⚑ Wedding and Corporate need new posts. Open the row to see why and copy a brief." */
export function needsNewPostsBanner(services: readonly BudgetService[]): string {
  const labels = services.map((s) => BUDGET_SERVICE_LABELS[s]);
  const names = labels.length <= 1 ? (labels[0] ?? "") : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
  return `⚑ ${names} ${labels.length > 1 ? "need" : "needs"} new posts. Open the row to see why and copy a brief.`;
}

/** The plain-text brief "Copy a brief for 3 new posts" puts on the clipboard. */
export function postBrief(service: BudgetService, winning: readonly ContentTypeTag[]): string {
  const types = typesPhrase(winning);
  return [
    `Brief: 3 new ${BUDGET_SERVICE_LABELS[service]} posts for Tristano's Catering.`,
    ...(types ? [`- Show ${types}: these win most for other services.`] : []),
    `- End with a link to the quote planner: ${planLinkForService(service)}`,
    "- Skip giveaways, contests, dates and limited-time offers: those can't be tested.",
  ].join("\n");
}

/** A Postgres error from set_social_post_test_exclusion / stop_creative_test, for a toast. */
export function rpcErrorMessage(err: unknown): string {
  const e = (err && typeof err === "object" ? err : {}) as { code?: unknown; message?: unknown };
  const message = typeof e.message === "string" ? e.message : err instanceof Error ? err.message : String(err);
  if (e.code === "42501") return "You don't have permission to do that.";
  if (e.code === "22023" && message.includes("not_live")) return "Stopping a test needs Live mode.";
  if (e.code === "P0002")
    return message.includes("social post") ? "That post is gone. The page has refreshed." : "This test isn't running any more. The page has refreshed.";
  return message;
}

